import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { Series } from "./sermon-data";
import * as Language from "./languages";

import type { FilterOpts } from "./sermons-list-component";


export interface SearchEvent {
	filterOpts: FilterOpts,
}


@customElement("seach-sidebar")
export class SearchSidebar extends LitElement {
	static styles = css`
		* {
			box-sizing: border-box;
		}

		aside {
			--translate-x: -100%;
			--translate-y: -50%;

			position: fixed;
			top: 50%;
			bottom: 0;
			width: 300px;
			max-width: 75vw;
			min-height: 300px;
			background-color: rgb(255 255 255 / 96%);
			backdrop-filter: blur(3px);
			z-index: 10;
			padding: 16px;
			border: 1px solid hsl(263deg 45% 56% / 50%);
			border-left: none;
			border-top-right-radius: 32px;
			border-bottom-right-radius: 32px;
			transition: all 500ms;
			transform: translate(var(--translate-x), var(--translate-y));
			box-shadow: none;
			display: flex;
			flex-direction: column;
		}

		aside.expanded {
			--translate-x: 0;
			box-shadow: rgb(131 92 193 / 50%) 1px 1px 10px 0px;
		}

		aside .header {
			position: relative;
			padding-bottom: 8px;
			margin-bottom: 24px;
			display: flex;
			align-items: center;
		}

		aside h2 {
			margin: 0;
			text-align: center;
			flex: 1;
		}

		aside .header::after {
			position: absolute;
			content: "";
			left: 5%;
			right: 5%;
			bottom: 0;
			height: 1px;
			background: linear-gradient(
				to right,
				transparent,
				gray,
				transparent
			);
		}

		.fa-undo-alt {
			color: hsl(263deg 45% 56%);
			background-color: white;
			width: 32px;
			height: 32px;
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 50%;
			border: 1px solid hsl(263deg 45% 56%);
			cursor: pointer;
			transition: all 250ms;
		}

		.fa-undo-alt:hover {
			background-color: hsl(263deg 45% 56%);
			color: white;
		}

		aside .handle {
			position: absolute;
			right: 0;
			top: 50%;
			transform: translateX(100%) translateY(-50%);
			border-top-right-radius: 50%;
			border-bottom-right-radius: 50%;
			border: 1px solid wheat;
			border-left: none;
			background-color: hsl(263deg 45% 56%);
			color: white;
			padding: 16px;
			padding-right: 24px; /* compensate for right border-radius */
			cursor: pointer;
			transition: background-color 250ms;
		}

		aside .handle:hover {
			background-color: hsl(263deg 45% 64%);
		}

		form {
			display: flex;
			flex-direction: column;
			flex: 1;
			overflow-y: auto;
		}

		.form-controls {
			flex: 1;
			display: flex;
			flex-direction: column;
			overflow-y: auto;
			overscroll-behavior-y: contain;
		}

		.form-controls > *:first-child {
			margin-top: 0;
		}

		.form-controls > *:last-child {
			margin-bottom: 16px;
		}

		label {
			display: flex;
			flex-direction: column;
			margin: 16px 0;
		}

		input {
			appearance: none;
			-webkit-appearance: none;
		}

		input:focus-visible {
			outline-color: hsl(263deg 45% 56%);
		}

		input[type="submit"] {
			display: flex;
			justify-content: center;
			outline: none;
			border: none;
			width: 75%;
			margin-left: auto;
			margin-right: auto;
			padding: 0.5em;
			background-color: hsl(263deg 45% 56%);
			color: white;
			border-radius: 16px;
			font-size: medium;
			cursor: pointer;
			transition: background-color 250ms;
			margin-top: 8px;
		}

		input[type="submit"]:hover {
			background-color: hsl(263deg 45% 64%);
		}

		input[type="submit"]:focus {
			outline: 1px solid black;
		}

		select:focus-visible {
			outline-color: hsl(263deg 45% 56%);
		}
	`;

	@property({ attribute: false })
	public allSeries: Series[] = [];

	@property({ attribute: false })
	public allLanguages: string[] = [];

	@state()
	private sidebarExpanded = false;

	@state()
	private sermonTitle: string|null = null;

	@state()
	private selectedSeries: Series|null = null;

	@state()
	private selectedLanguage: string|null = null;


	private handleSidebarToggleClick() {
		this.sidebarExpanded = !this.sidebarExpanded;
	}

	private handleTitleInput(ev: InputEvent) {
		if (!(ev.target instanceof HTMLInputElement)) { return; }

		const { value } = ev.target;

		this.sermonTitle = (value === "" ? null : value);
	}

	private handleSeriesInput(ev: InputEvent) {
		if (!(ev.target instanceof HTMLSelectElement)) { return; }

		const { value } = ev.target;

		if (value === "") {
			this.selectedSeries = null;
		} else {
			const seriesId = parseInt(value, 10);
			if (Number.isNaN(seriesId)) { throw new Error(); }

			const series = this.allSeries.find(s => s.id === seriesId);
			if (series === undefined) { throw new Error(); }

			this.selectedSeries = series;
		}
	}

	private handleLanguageInput(ev: InputEvent) {
		if (!(ev.target instanceof HTMLSelectElement)) { return; }

		const { value } = ev.target;

		if (value === "") {
			this.selectedLanguage = null;
		} else {
			this.selectedLanguage = value;
		}
	}

	private handleFormSubmit(ev: SubmitEvent) {
		ev.preventDefault();
		this.sidebarExpanded = false;

		const filterOpts: FilterOpts = {
			title: this.sermonTitle,
			series: this.selectedSeries,
			language: this.selectedLanguage,
		};

		const event = new CustomEvent<SearchEvent>("onSearch", {
			composed: true,
			detail: {
				filterOpts,
			},
		});

		this.dispatchEvent(event);
	}

	private handleClearClick() {
		this.sermonTitle = null;
		this.selectedSeries = null;
		this.selectedLanguage = null;
	}


	render() {
		return html`
			<!-- Font Awesome -->
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css" integrity="sha512-iBBXm8fW90+nuLcSKlbmrPcLa0OT92xO1BIsZ+ywDWZCvqsWgccV3gFoRBv0z+8dLJgyAHIhR35VZc2oM/gI1w==" crossorigin="anonymous" referrerpolicy="no-referrer" />

			<aside class=${classMap({
				"expanded": this.sidebarExpanded,
			})}>
				<div class="header">
					<button
						class = "fas fa-fw fa-undo-alt"
						aria-label = "Reset Form"
						@click=${this.handleClearClick}
					></button>
					<h2>Search</h2>

					<!-- To perfectly center the h2, we insert a dummy element. -->
					<button class="fas fa-fw fa-undo-alt" style="visibility: hidden;"></button>
				</div>

				<form @submit=${this.handleFormSubmit}>
					<div class="form-controls">
						<label>
							<span>Title:</span>
							<input
								type = "text"
								.value = ${this.sermonTitle ?? ""}
								@input = ${this.handleTitleInput}
							/>
						</label>

						<label>
							<span>Series:</span>
							<select
								@input = ${this.handleSeriesInput}
								.value = ${this.selectedSeries?.id.toString() ?? ""}
							>
								<option value=""></option>
								${this.allSeries
									.sort((a, b) => a.name.localeCompare(b.name))
									.map(s => html`
										<option value=${s.id}>${s.name}</option>
									`)
								}
							</select>
						</label>

						<label>
							<span>Language:</span>
							<select
								@input = ${this.handleLanguageInput}
								.value = ${this.selectedLanguage ?? ""}
							>
								<option value=""></option>
								${this.allLanguages
									.map(l => html`
										<option value=${l}>
											${Language.toDefaultFormat(l)}
										</option>
									`)
								}
							</select>
						</label>
					</div>

					<input
						type = "submit"
						value = "Submit"
					/>
				</form>

				<button
					class = "handle"
					aria-label = "Search"
					@click = ${this.handleSidebarToggleClick}
				>
					<i class="fas fa-search"></i>
				</button>
			</aside>
		`;
	}
}